import type { MarketPoint, MarketSnapshot, WeeklySummary } from "../market-data/types";
import type { LoadedSkinAssets } from "../skins/assets";
import type { MotionSkin } from "../skins/types";
import { hexToRgba } from "./effects";
import { money, signedMoney, signedPercent } from "./formatting";
import { DEFAULT_RENDER_SETTINGS, getGlowMultiplier, type MotionRenderSettings } from "./render-settings";
import { DEFAULT_GRAPH, type ScaleState, xToPx, yToPx } from "./scale";

const W = 1080;
const H = 1080;

type RenderArgs = {
  ctx: CanvasRenderingContext2D;
  snapshot: MarketSnapshot;
  skin: MotionSkin;
  points: MarketPoint[];
  scale: ScaleState;
  visibleCount: number;
  finishProgress: number;
  holdProgress: number;
  motionProgress: number;
  scenarioLabel: string;
  settings?: MotionRenderSettings;
  assets?: LoadedSkinAssets;
};

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

function fillRounded(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string,
  stroke?: string,
  strokeWidth = 1.5
) {
  roundedRect(ctx, x, y, w, h, r);
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    const inset = strokeWidth / 2;
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    roundedRect(ctx, x + inset, y + inset, w - strokeWidth, h - strokeWidth, Math.max(0, r - inset));
    ctx.stroke();
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function decimalsFor(snapshot: Pick<MarketSnapshot, "decimals" | "currency">): number {
  if (typeof snapshot.decimals === "number") return snapshot.decimals;
  return snapshot.currency === "JPY" ? 0 : 2;
}

function dateJa(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  if (!year || !month || !day) return date;
  return `${year}年${month}月${day}日`;
}

function shortDateJa(date: string): string {
  const [, month, day] = date.split("-").map(Number);
  if (!month || !day) return date;
  return `${month}/${day}`;
}

function scenarioJa(scenario: string): string {
  if (scenario === "actual") return "通常";
  if (scenario === "surge") return "急騰デモ";
  if (scenario === "crash") return "急落デモ";
  if (scenario === "manual") return "手動入力";
  return scenario;
}

function drawSeasonalBackgroundAsset(ctx: CanvasRenderingContext2D, skin: MotionSkin, assets: LoadedSkinAssets | undefined) {
  const image = assets?.background;
  if (!image) return;
  ctx.save();
  ctx.globalAlpha = skin.decoration === "football" ? 0.64 : skin.decoration === "summer" ? 0.58 : 0.48;
  ctx.drawImage(image, 0, 0, W, H);
  ctx.restore();
}

function drawDecorations(ctx: CanvasRenderingContext2D, skin: MotionSkin, progress: number, assets?: LoadedSkinAssets) {
  drawSeasonalBackgroundAsset(ctx, skin, assets);
  ctx.save();
  if (skin.decoration === "neon") {
    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < 18; i += 1) {
      const x = DEFAULT_GRAPH.x0 + ((progress * 420 + i * 89) % (DEFAULT_GRAPH.x1 - DEFAULT_GRAPH.x0));
      const y = DEFAULT_GRAPH.y0 + 35 + ((progress * 260 + i * 57) % (DEFAULT_GRAPH.y1 - DEFAULT_GRAPH.y0 - 70));
      ctx.fillStyle = `rgba(130,220,255,${0.035 + (i % 5) * 0.012})`;
      ctx.beginPath();
      ctx.arc(x, y, 2 + (i % 3), 0, Math.PI * 2);
      ctx.fill();
    }
  }
  if (skin.decoration === "rainy") {
    const cloud = ctx.createLinearGradient(0, 0, 0, 320);
    cloud.addColorStop(0, "rgba(118,145,176,.24)");
    cloud.addColorStop(1, "rgba(118,145,176,0)");
    ctx.fillStyle = cloud;
    ctx.fillRect(0, 0, W, 330);
    ctx.strokeStyle = "rgba(80,120,160,.22)";
    ctx.lineWidth = 2;
    for (let i = 0; i < 42; i += 1) {
      const x = (i * 41 + progress * 220) % W;
      const y = (i * 83 + progress * 560) % H;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x - 10, y + 28);
      ctx.stroke();
    }
    ctx.fillStyle = "rgba(128,108,210,.22)";
    ctx.beginPath();
    ctx.arc(890, 140, 52, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(120,170,220,.20)";
    ctx.beginPath();
    ctx.arc(930, 170, 38, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(74,91,130,.20)";
    ctx.lineWidth = 4;
    for (let i = 0; i < 6; i += 1) {
      ctx.beginPath();
      ctx.ellipse(120 + i * 56, 958 + Math.sin(progress * 5 + i) * 5, 28, 7, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.fillStyle = "rgba(110,92,190,.24)";
    for (let i = 0; i < 5; i += 1) {
      ctx.beginPath();
      ctx.arc(120 + i * 18, 130 + (i % 2) * 16, 14, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = "rgba(74,110,88,.28)";
    ctx.lineWidth = 4;
    for (let i = 0; i < 4; i += 1) {
      const baseX = 144 + i * 42;
      const baseY = 164 + Math.sin(progress * 3 + i) * 4;
      ctx.beginPath();
      ctx.moveTo(baseX, baseY + 34);
      ctx.lineTo(baseX + 8, baseY + 6);
      ctx.stroke();
      for (let petal = 0; petal < 6; petal += 1) {
        const angle = (petal / 6) * Math.PI * 2;
        ctx.fillStyle = petal % 2 === 0 ? "rgba(126,104,205,.32)" : "rgba(94,154,205,.28)";
        ctx.beginPath();
        ctx.ellipse(baseX + Math.cos(angle) * 14, baseY + Math.sin(angle) * 10, 10, 7, angle, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  if (skin.decoration === "summer") {
    const sea = ctx.createLinearGradient(0, 625, 0, H);
    sea.addColorStop(0, "rgba(40,177,210,.06)");
    sea.addColorStop(0.42, "rgba(34,155,198,.20)");
    sea.addColorStop(0.72, "rgba(255,209,130,.18)");
    sea.addColorStop(1, "rgba(255,236,180,.26)");
    ctx.fillStyle = sea;
    ctx.fillRect(0, 610, W, H - 610);
    ctx.fillStyle = "rgba(255,184,74,.55)";
    ctx.beginPath();
    ctx.arc(900, 130, 54, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,184,74,.35)";
    ctx.lineWidth = 5;
    for (let i = 0; i < 12; i += 1) {
      const angle = (i / 12) * Math.PI * 2 + progress;
      ctx.beginPath();
      ctx.moveTo(900 + Math.cos(angle) * 72, 130 + Math.sin(angle) * 72);
      ctx.lineTo(900 + Math.cos(angle) * 94, 130 + Math.sin(angle) * 94);
      ctx.stroke();
    }
    ctx.strokeStyle = "rgba(45,150,190,.22)";
    ctx.lineWidth = 6;
    for (let i = 0; i < 5; i += 1) {
      ctx.beginPath();
      ctx.arc(80 + i * 230, 900 + Math.sin(progress * 4 + i) * 10, 90, Math.PI * 1.05, Math.PI * 1.95);
      ctx.stroke();
    }
    ctx.fillStyle = "rgba(18,62,82,.25)";
    for (let i = 0; i < 6; i += 1) {
      const x = 150 + i * 138;
      const y = 835 + (i % 2) * 18;
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = "rgba(18,62,82,.24)";
      ctx.beginPath();
      ctx.moveTo(x, y + 9);
      ctx.lineTo(x, y + 34);
      ctx.moveTo(x - 14, y + 20);
      ctx.lineTo(x + 14, y + 20);
      ctx.moveTo(x, y + 34);
      ctx.lineTo(x - 10, y + 49);
      ctx.moveTo(x, y + 34);
      ctx.lineTo(x + 10, y + 49);
      ctx.stroke();
    }
    ctx.strokeStyle = "rgba(255,255,255,.34)";
    ctx.lineWidth = 4;
    for (let i = 0; i < 9; i += 1) {
      ctx.beginPath();
      ctx.arc(790 + i * 20, 700 + Math.sin(progress * 8 + i) * 9, 8 + (i % 3), 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  if (skin.decoration === "football") {
    const pitch = ctx.createLinearGradient(0, 650, 0, H);
    pitch.addColorStop(0, "rgba(22,146,82,.16)");
    pitch.addColorStop(1, "rgba(15,112,68,.34)");
    ctx.fillStyle = pitch;
    ctx.fillRect(0, 640, W, H - 640);
    ctx.strokeStyle = "rgba(255,255,255,.22)";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(92, 798);
    ctx.lineTo(988, 798);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(540, 798, 160, Math.PI * 1.03, Math.PI * 1.97);
    ctx.stroke();
    ctx.fillStyle = "rgba(255,255,255,.55)";
    for (const x of [128, 952]) {
      ctx.beginPath();
      ctx.arc(x, 118, 46 + Math.sin(progress * 5) * 2, 0, Math.PI * 2);
      ctx.fill();
    }
    for (let i = 0; i < 34; i += 1) {
      const x = (i * 71 + progress * 110) % W;
      const y = 428 + (i * 37) % 150;
      const color = i % 3 === 0 ? "#ffffff" : i % 3 === 1 ? "#2c72e8" : "#f53346";
      ctx.fillStyle = hexToRgba(color, 0.18);
      ctx.fillRect(x, y, 18, 4);
    }
  }
  ctx.restore();
}

function drawDirectionalSeason(ctx: CanvasRenderingContext2D, skin: MotionSkin, isUp: boolean, progress: number) {
  ctx.save();
  if (skin.decoration === "rainy") {
    if (isUp) {
      ctx.lineWidth = 10;
      for (const [index, color] of ["#ef6a8a", "#ffb84a", "#22c55e", "#38a3db", "#7f6bd6"].entries()) {
        ctx.strokeStyle = hexToRgba(color, 0.24);
        ctx.beginPath();
        ctx.arc(828, 250, 124 + index * 15, Math.PI * 1.05, Math.PI * 1.86);
        ctx.stroke();
      }
    } else {
      ctx.strokeStyle = "rgba(70,90,145,.30)";
      ctx.lineWidth = 3;
      for (let i = 0; i < 26; i += 1) {
        const x = 760 + (i * 19) % 250;
        const y = 210 + ((i * 47 + progress * 780) % 430);
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - 14, y + 36);
        ctx.stroke();
      }
    }
    ctx.fillStyle = "rgba(76,65,118,.16)";
    ctx.beginPath();
    ctx.arc(884, 682, 52, Math.PI, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(76,65,118,.24)";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(884, 682);
    ctx.quadraticCurveTo(882, 735, 916, 724);
    ctx.stroke();
  }
  if (skin.decoration === "summer") {
    if (isUp) {
      ctx.fillStyle = "rgba(255,184,74,.28)";
      ctx.beginPath();
      ctx.arc(858, 242, 88 + Math.sin(progress * 8) * 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(26,164,201,.22)";
      ctx.lineWidth = 7;
      for (let i = 0; i < 3; i += 1) {
        ctx.beginPath();
        ctx.arc(760 + i * 92, 654 + Math.sin(progress * 6 + i) * 8, 44, Math.PI * 1.12, Math.PI * 1.9);
        ctx.stroke();
      }
    } else {
      ctx.fillStyle = "rgba(68,94,132,.18)";
      ctx.beginPath();
      ctx.ellipse(846, 238, 128, 48, 0.08, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(45,111,153,.24)";
      ctx.lineWidth = 8;
      for (let i = 0; i < 4; i += 1) {
        ctx.beginPath();
        ctx.arc(744 + i * 78, 682 + Math.sin(progress * 7 + i) * 12, 52, Math.PI * 1.05, Math.PI * 1.88);
        ctx.stroke();
      }
    }
  }
  if (skin.decoration === "football") {
    const goalX = 790;
    const goalY = 690;
    ctx.strokeStyle = "rgba(255,255,255,.58)";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(goalX, goalY + 146);
    ctx.lineTo(goalX, goalY);
    ctx.lineTo(goalX + 210, goalY);
    ctx.lineTo(goalX + 210, goalY + 146);
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(255,255,255,.26)";
    for (let i = 1; i < 8; i += 1) {
      ctx.beginPath();
      ctx.moveTo(goalX + i * 26, goalY);
      ctx.lineTo(goalX + i * 26, goalY + 146);
      ctx.stroke();
    }
    if (isUp) {
      ctx.strokeStyle = "rgba(255,255,255,.60)";
      ctx.lineWidth = 6;
      for (const angle of [-0.5, -0.18, 0.18, 0.5]) {
        ctx.beginPath();
        ctx.moveTo(900, 742);
        ctx.lineTo(900 + Math.cos(angle) * 132, 742 + Math.sin(angle) * 72);
        ctx.stroke();
      }
      ctx.fillStyle = "rgba(245,51,70,.30)";
      ctx.beginPath();
      ctx.arc(908, 742, 38 + Math.sin(progress * 8) * 4, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.strokeStyle = "rgba(210,224,250,.34)";
      ctx.lineWidth = 7;
      ctx.beginPath();
      ctx.arc(900, 654, 92, Math.PI * 1.08, Math.PI * 1.85);
      ctx.stroke();
    }
  }
  ctx.restore();
}

type AxisOptions = {
  referenceValue: number;
  referenceLabel: string;
  referenceDecimals: number;
  xTicks: Array<{ x: number; label: string }>;
};

const DAILY_TIME_TICKS = [
  { x: 0, label: "9:30" },
  { x: 0.23, label: "11:00" },
  { x: 0.54, label: "13:00" },
  { x: 0.77, label: "14:30" },
  { x: 1, label: "16:00" }
];

function drawAxis(ctx: CanvasRenderingContext2D, skin: MotionSkin, scale: ScaleState, options: AxisOptions) {
  const graphMidY = (DEFAULT_GRAPH.y0 + DEFAULT_GRAPH.y1) / 2;
  ctx.strokeStyle = skin.palette.grid;
  ctx.lineWidth = 1;
  for (let i = 0; i < 6; i += 1) {
    const x = DEFAULT_GRAPH.x0 + (DEFAULT_GRAPH.x1 - DEFAULT_GRAPH.x0) * i / 5;
    ctx.beginPath();
    ctx.moveTo(x, DEFAULT_GRAPH.y0);
    ctx.lineTo(x, DEFAULT_GRAPH.y1);
    ctx.stroke();
  }
  for (let i = 0; i < 5; i += 1) {
    const y = DEFAULT_GRAPH.y0 + (DEFAULT_GRAPH.y1 - DEFAULT_GRAPH.y0) * i / 4;
    ctx.beginPath();
    ctx.moveTo(DEFAULT_GRAPH.x0, y);
    ctx.lineTo(DEFAULT_GRAPH.x1, y);
    ctx.stroke();
  }
  ctx.strokeStyle = hexToRgba(skin.palette.accent, 0.7);
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(DEFAULT_GRAPH.x0, graphMidY);
  ctx.lineTo(DEFAULT_GRAPH.x1, graphMidY);
  ctx.stroke();

  ctx.fillStyle = skin.palette.muted;
  ctx.font = `600 18px ${skin.typography.family}`;
  ctx.textAlign = "left";
  for (let i = -2; i <= 2; i += 1) {
    const value = options.referenceValue + (scale.halfRange / 2) * i;
    ctx.fillText(money(value, options.referenceDecimals), DEFAULT_GRAPH.x0 + 12, yToPx(value, scale) - 5);
  }
  ctx.textAlign = "center";
  for (const tick of options.xTicks) {
    ctx.textAlign = tick.x === 0 ? "left" : tick.x === 1 ? "right" : "center";
    ctx.fillText(tick.label, xToPx(tick.x), DEFAULT_GRAPH.y1 + 32);
  }
  ctx.fillStyle = hexToRgba(skin.palette.accent, 0.95);
  ctx.textAlign = "right";
  ctx.font = `700 18px ${skin.typography.family}`;
  ctx.fillText(`${options.referenceLabel} ${money(options.referenceValue, options.referenceDecimals)}`, DEFAULT_GRAPH.x1 - 14, graphMidY - 10);
  ctx.textAlign = "left";
}

function drawPath(ctx: CanvasRenderingContext2D, skin: MotionSkin, points: MarketPoint[], scale: ScaleState, visibleCount: number, referenceValue: number) {
  if (visibleCount < 2) return;
  const layers = skin.decoration === "neon"
    ? [
      { width: 13, alpha: 0.045 },
      { width: 7, alpha: 0.11 },
      { width: 4, alpha: 0.28 },
      { width: 2.4, alpha: 0.96 }
    ]
    : [
      { width: 3.2, alpha: 0.96 }
    ];
  for (const layer of layers) {
    ctx.lineWidth = layer.width;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (let i = 1; i < visibleCount; i += 1) {
      const a = points[i - 1];
      const b = points[i];
      const color = b.y >= referenceValue ? skin.palette.up : skin.palette.down;
      ctx.strokeStyle = hexToRgba(color, layer.alpha);
      ctx.beginPath();
      ctx.moveTo(xToPx(a.x), yToPx(a.y, scale));
      ctx.lineTo(xToPx(b.x), yToPx(b.y, scale));
      ctx.stroke();
    }
  }
}

function drawTerminalGlow(
  ctx: CanvasRenderingContext2D,
  skin: MotionSkin,
  x: number,
  y: number,
  color: string,
  isUp: boolean,
  finishProgress: number,
  holdProgress: number,
  settings: MotionRenderSettings = DEFAULT_RENDER_SETTINGS
) {
  if (skin.decoration !== "neon") return;
  const glow = getGlowMultiplier(settings);
  const runningPulse = 0.5 + 0.5 * Math.sin(performance.now() / 150);
  const holdPulse = 0.45 + 0.12 * Math.sin(holdProgress * Math.PI * 1.8);
  const pulse = holdProgress > 0 ? holdPulse : runningPulse;
  const brightness = isUp ? 0.82 + finishProgress * 0.42 : 0.72 - finishProgress * 0.2;
  const radiusBoost = (0.78 + finishProgress * (isUp ? 0.16 : 0.06)) * (0.9 + glow * 0.1);

  ctx.save();
  ctx.globalCompositeOperation = "lighter";

  for (let i = 0; i < 4; i += 1) {
    const radius = (32 + i * 18 + pulse * 12) * radiusBoost;
    ctx.strokeStyle = hexToRgba(isUp ? skin.palette.accent : color, Math.max(0, 0.14 - i * 0.027) * brightness * glow);
    ctx.lineWidth = (1.9 - i * 0.16) * (0.9 + glow * 0.1);
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.stroke();
  }

  const outerRadius = (92 + pulse * 16) * radiusBoost;
  const outer = ctx.createRadialGradient(x, y, 0, x, y, outerRadius);
  outer.addColorStop(0, `rgba(255,255,255,${0.22 * glow})`);
  outer.addColorStop(0.18, hexToRgba(color, 0.22 * brightness * glow));
  outer.addColorStop(0.45, hexToRgba(color, 0.09 * brightness * glow));
  outer.addColorStop(1, hexToRgba(color, 0));
  ctx.fillStyle = outer;
  ctx.beginPath();
  ctx.arc(x, y, outerRadius, 0, Math.PI * 2);
  ctx.fill();

  const coreRadius = (30 + pulse * 6) * radiusBoost;
  const core = ctx.createRadialGradient(x, y, 0, x, y, coreRadius);
  core.addColorStop(0, hexToRgba(color, 0.36 * brightness * glow));
  core.addColorStop(0.34, hexToRgba(color, 0.2 * brightness * glow));
  core.addColorStop(0.72, hexToRgba(color, 0.1 * brightness * glow));
  core.addColorStop(1, hexToRgba(color, 0));
  ctx.fillStyle = core;
  ctx.beginPath();
  ctx.arc(x, y, coreRadius, 0, Math.PI * 2);
  ctx.fill();

  ctx.strokeStyle = `rgba(255,255,255,${(0.08 + pulse * 0.06) * brightness * glow})`;
  ctx.lineWidth = 2.2 * (0.9 + glow * 0.1);
  ctx.lineCap = "round";
  for (const [length, angle] of [[72, 0], [48, Math.PI / 2], [42, Math.PI / 4], [42, -Math.PI / 4]]) {
    const l = Number(length) * radiusBoost;
    const a = Number(angle);
    ctx.beginPath();
    ctx.moveTo(x - Math.cos(a) * l, y - Math.sin(a) * l);
    ctx.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l);
    ctx.stroke();
  }

  ctx.restore();
}

function tipSizeForSkin(skin: MotionSkin): number {
  if (skin.decoration === "summer") return 92;
  if (skin.decoration === "football") return 104;
  return 116;
}

export function computeTipPlacement(
  x: number,
  y: number,
  size: number,
  preferred: "up" | "down" = "up"
): { left: number; top: number; size: number } {
  const rawLeft = x - size * 0.82;
  const rawTop = preferred === "up" ? y - size * 1.12 : y + size * 0.18;
  const safe = 18;
  return {
    left: clamp(rawLeft, safe, W - size - safe),
    top: clamp(rawTop, safe, H - size - safe),
    size
  };
}

function drawSkinTipAsset(
  ctx: CanvasRenderingContext2D,
  skin: MotionSkin,
  assets: LoadedSkinAssets | undefined,
  x: number,
  y: number,
  isUp: boolean,
  progress: number
) {
  const image = (isUp ? assets?.tipUp : assets?.tipDown) ?? assets?.tipIdle;
  if (!image) return false;
  const size = tipSizeForSkin(skin);
  const bob = Math.sin(progress * 5) * 4;
  const placement = computeTipPlacement(x, y + bob, size, isUp ? "up" : "down");
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.translate(placement.left + size / 2, placement.top + size / 2);
  const settle = Math.max(0, progress - 0.7) / 0.3;
  const nod = isUp ? Math.sin(progress * 8) * 0.035 * settle : Math.sin(progress * 5) * 0.018 * settle - 0.08 * settle;
  ctx.rotate(nod);
  ctx.drawImage(image, -size / 2, -size / 2, size, size);
  ctx.restore();
  return true;
}

function drawTip(
  ctx: CanvasRenderingContext2D,
  snapshot: MarketSnapshot,
  skin: MotionSkin,
  point: MarketPoint,
  scale: ScaleState,
  finishProgress: number,
  holdProgress: number,
  settings: MotionRenderSettings,
  assets?: LoadedSkinAssets
) {
  const x = xToPx(point.x);
  const y = yToPx(point.y, scale);
  const finalChange = snapshot.close - snapshot.previousClose;
  const color = point.y >= snapshot.previousClose ? skin.palette.up : skin.palette.down;
  const expressionUp = finishProgress > 0.65 ? finalChange >= 0 : point.y >= snapshot.previousClose;
  ctx.save();
  drawTerminalGlow(ctx, skin, x, y, color, finalChange >= 0, finishProgress, holdProgress, settings);
  if (!drawSkinTipAsset(ctx, skin, assets, x, y, expressionUp, finishProgress + holdProgress)) {
    drawSkinTip(ctx, skin, x, y, expressionUp, finishProgress + holdProgress);
  }
  ctx.restore();
}

function drawFootballReactionFallback(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, isUp: boolean, progress: number) {
  ctx.save();
  ctx.translate(x + size / 2, y + size / 2 + Math.sin(progress * 7) * (isUp ? 5 : 2));
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.fillStyle = "#f7f3ea";
  ctx.strokeStyle = "#132342";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(0, -24, 21, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "#2868c8";
  ctx.beginPath();
  ctx.roundRect?.(-24, -2, 48, 44, 10);
  if (!ctx.roundRect) {
    roundedRect(ctx, -24, -2, 48, 44, 10);
  }
  ctx.fill();
  ctx.stroke();
  ctx.strokeStyle = "#f14857";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(-14, 0);
  ctx.lineTo(0, 10);
  ctx.lineTo(14, 0);
  ctx.stroke();
  ctx.strokeStyle = "#132342";
  ctx.lineWidth = 5;
  ctx.beginPath();
  if (isUp) {
    ctx.moveTo(-21, 10);
    ctx.lineTo(-42, -18);
    ctx.moveTo(21, 10);
    ctx.lineTo(42, -18);
    ctx.moveTo(-10, 42);
    ctx.lineTo(-18, 70);
    ctx.moveTo(10, 42);
    ctx.lineTo(18, 70);
  } else {
    ctx.moveTo(-22, 11);
    ctx.lineTo(-36, 35);
    ctx.moveTo(22, 11);
    ctx.lineTo(36, 35);
    ctx.moveTo(-10, 42);
    ctx.lineTo(-16, 70);
    ctx.moveTo(10, 42);
    ctx.lineTo(16, 70);
  }
  ctx.stroke();
  ctx.fillStyle = "#132342";
  ctx.beginPath();
  ctx.arc(-8, -26, 2.5, 0, Math.PI * 2);
  ctx.arc(8, -26, 2.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#132342";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  if (isUp) ctx.arc(0, -19, 8, 0.18, Math.PI - 0.18);
  else ctx.arc(0, -13, 8, Math.PI + 0.18, Math.PI * 2 - 0.18);
  ctx.stroke();
  ctx.restore();
}

function drawReactionAccentAsset(
  ctx: CanvasRenderingContext2D,
  skin: MotionSkin,
  assets: LoadedSkinAssets | undefined,
  isUp: boolean,
  finishProgress: number,
  holdProgress: number
) {
  if (skin.decoration !== "football" || finishProgress < 0.55) return;
  const alpha = clamp((finishProgress - 0.55) / 0.3, 0, 1);
  const image = isUp ? assets?.accentUp : assets?.accentDown;
  const size = isUp ? 142 : 136;
  const left = isUp ? 792 : 806;
  const top = 68 + Math.sin((finishProgress + holdProgress) * 7) * (isUp ? 5 : 2);
  ctx.save();
  ctx.globalAlpha = alpha;
  if (image) {
    ctx.drawImage(image, left, top, size, size);
  } else {
    drawFootballReactionFallback(ctx, left, top, size, isUp, finishProgress + holdProgress);
  }
  ctx.restore();
}

function drawSkinTip(ctx: CanvasRenderingContext2D, skin: MotionSkin, x: number, y: number, isUp: boolean, progress: number) {
  const size = tipSizeForSkin(skin);
  const placement = computeTipPlacement(x, y + Math.sin(progress * 5) * 4, size, isUp ? "up" : "down");
  ctx.save();
  ctx.globalCompositeOperation = "source-over";
  ctx.translate(placement.left + size * 0.5, placement.top + size * 0.55);
  if (skin.decoration === "rainy") {
    ctx.translate(-18, -24 + Math.sin(progress * 5) * 2);
    const coat = isUp ? "#ffe66d" : "#7f91e8";
    ctx.fillStyle = isUp ? "rgba(255,235,115,.96)" : "rgba(127,145,232,.96)";
    ctx.beginPath();
    ctx.arc(-2, 28, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = coat;
    ctx.beginPath();
    ctx.moveTo(-18, 42);
    ctx.quadraticCurveTo(-2, 22, 14, 42);
    ctx.lineTo(20, 66);
    ctx.lineTo(-24, 66);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = isUp ? "rgba(34,197,94,.72)" : "rgba(91,114,214,.75)";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.arc(0, 5, 26, Math.PI, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = isUp ? "rgba(210,255,220,.35)" : "rgba(215,226,255,.45)";
    ctx.beginPath();
    ctx.arc(0, 5, 26, Math.PI, Math.PI * 2);
    ctx.lineTo(26, 5);
    ctx.lineTo(-26, 5);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "rgba(70,82,110,.75)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(0, 5);
    ctx.quadraticCurveTo(0, 28, 16, 22);
    ctx.stroke();
    ctx.fillStyle = "#26324a";
    ctx.beginPath();
    ctx.arc(-7, 27, 2.2, 0, Math.PI * 2);
    ctx.arc(4, 27, 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "#26324a";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(-1, 33, 5, 0.2, Math.PI - 0.2);
    ctx.stroke();
    ctx.strokeStyle = "rgba(38,50,74,.45)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(-12, 66);
    ctx.lineTo(-18, 78);
    ctx.moveTo(8, 66);
    ctx.lineTo(15, 78);
    ctx.stroke();
    if (!isUp) {
      ctx.strokeStyle = "rgba(91,114,214,.5)";
      ctx.lineWidth = 2;
      for (let i = 0; i < 5; i += 1) {
        ctx.beginPath();
        ctx.moveTo(18 + i * 6, -4 + i * 5);
        ctx.lineTo(10 + i * 6, 16 + i * 5);
        ctx.stroke();
      }
    }
  } else if (skin.decoration === "summer") {
    ctx.translate(-18, -18 + Math.sin(progress * 6) * 2);
    ctx.rotate(-0.18 + Math.sin(progress * 4) * 0.08);
    ctx.fillStyle = "#1f9d55";
    ctx.beginPath();
    ctx.arc(0, 0, 24, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = isUp ? "#ff4d62" : "#ff6b78";
    ctx.beginPath();
    ctx.arc(0, 0, 19, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(245,255,220,.86)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(0, 0, 21, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = "#243447";
    for (let i = 0; i < 6; i += 1) {
      const angle = -Math.PI * 0.78 + i * 0.32;
      ctx.beginPath();
      ctx.ellipse(Math.cos(angle) * 9, Math.sin(angle) * 9, 2, 4, angle, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.strokeStyle = "rgba(255,255,255,.78)";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(-7, -9, 7, Math.PI * 1.1, Math.PI * 1.65);
    ctx.stroke();
    ctx.rotate(0.18 - Math.sin(progress * 4) * 0.08);
    ctx.strokeStyle = "rgba(20,164,201,.45)";
    ctx.lineWidth = 3;
    for (let i = 0; i < 3; i += 1) {
      ctx.beginPath();
      ctx.arc(-30 + i * 18, 24 + Math.sin(progress * 4 + i) * 3, 9, Math.PI, Math.PI * 2);
      ctx.stroke();
    }
  } else if (skin.decoration === "football") {
    ctx.translate(-10, -8 + Math.sin(progress * 5) * 2);
    ctx.rotate(progress * (isUp ? 0.45 : -0.25));
    const r = 34;
    ctx.fillStyle = "#fbfaf4";
    ctx.strokeStyle = "#1d304a";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    for (const [index, color] of ["#f53346", "#158553", "#2767d8"].entries()) {
      const angle = progress * 0.7 + index * Math.PI * 2 / 3;
      ctx.strokeStyle = color;
      ctx.lineWidth = 8;
      ctx.beginPath();
      ctx.arc(Math.cos(angle) * 5, Math.sin(angle) * 5, r * 0.64, angle - 0.65, angle + 0.65);
      ctx.stroke();
      ctx.strokeStyle = "rgba(29,48,74,.55)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(Math.cos(angle) * 3, Math.sin(angle) * 3, r * 0.74, angle - 0.7, angle + 0.7);
      ctx.stroke();
    }
    ctx.rotate(-progress * (isUp ? 0.45 : -0.25));
    ctx.strokeStyle = isUp ? "rgba(255,255,255,.70)" : "rgba(199,215,239,.45)";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(48, isUp ? -30 : 30, 34, isUp ? Math.PI * 1.1 : Math.PI * 0.1, isUp ? Math.PI * 1.8 : Math.PI * 0.9);
    ctx.stroke();
  }
  ctx.restore();
}

function drawLiveNumbers(ctx: CanvasRenderingContext2D, snapshot: MarketSnapshot, skin: MotionSkin, currentValue: number, revealProgress: number) {
  const decimals = decimalsFor(snapshot);
  const currentChange = currentValue - snapshot.previousClose;
  const currentPct = (currentChange / snapshot.previousClose) * 100;
  const finalChange = snapshot.change ?? snapshot.close - snapshot.previousClose;
  const finalPct = snapshot.changeRate ?? (finalChange / snapshot.previousClose) * 100;
  const finalMode = revealProgress > 0.7;
  const values = [
    { label: finalMode ? "終値" : "現在値", value: finalMode ? money(snapshot.close, decimals) : money(currentValue, decimals), color: skin.palette.foreground },
    { label: finalMode ? "変動額" : "値動き", value: finalMode ? signedMoney(finalChange, decimals) : signedMoney(currentChange, decimals), color: finalChange < 0 ? skin.palette.down : skin.palette.up },
    { label: "騰落率", value: finalMode ? signedPercent(finalPct) : signedPercent(currentPct), color: finalChange < 0 ? skin.palette.down : skin.palette.up }
  ];
  const y = 786;
  const gap = 20;
  const cardW = (924 - gap * 2) / 3;
  values.forEach((card, i) => {
    const x = 78 + i * (cardW + gap);
    fillRounded(ctx, x, y, cardW, 142, 28, "rgba(255,255,255,.18)", "rgba(255,255,255,.16)");
    ctx.fillStyle = skin.palette.muted;
    ctx.font = `700 25px ${skin.typography.family}`;
    ctx.fillText(card.label, x + 28, y + 44);
    ctx.fillStyle = card.color;
    ctx.font = `800 ${i === 0 ? 50 : 56}px ${skin.typography.family}`;
    ctx.fillText(card.value, x + 28, y + 105);
  });
}
export function prepareWeeklyRenderPoints(points: MarketPoint[]): MarketPoint[] {
  const deduplicated: MarketPoint[] = [];
  for (const point of points) {
    const previous = deduplicated.at(-1);
    if (previous && Math.abs(previous.x - point.x) < 0.000001) {
      // Keep the official session open at x=0 and the official close at the terminal x.
      if (point.x > 0.000001) deduplicated[deduplicated.length - 1] = point;
      continue;
    }
    deduplicated.push(point);
  }

  if (deduplicated.length < 5) return deduplicated;
  return deduplicated.map((point, index) => {
    if (index === 0 || index === deduplicated.length - 1) return point;
    const prev = deduplicated[index - 1];
    const next = deduplicated[index + 1];
    return {
      x: point.x,
      y: prev.y * 0.22 + point.y * 0.56 + next.y * 0.22
    };
  });
}

function symbolDisplayName(symbolId: string): string {
  if (symbolId === "sp500") return "S&P 500";
  if (symbolId === "nasdaq100") return "NASDAQ 100";
  if (symbolId === "nikkei225") return "日経225";
  if (symbolId === "usdjpy") return "USD/JPY";
  if (symbolId === "btcusd") return "BTC/USD";
  return symbolId.toUpperCase();
}

export function renderDailyFrame(args: RenderArgs): void {
  const { ctx, snapshot, skin, points, scale, visibleCount, finishProgress, holdProgress, motionProgress, scenarioLabel, settings = DEFAULT_RENDER_SETTINGS, assets } = args;
  const currentPoint = points[Math.min(visibleCount - 1, points.length - 1)];
  const currentValue = currentPoint.y;
  const graphMidY = (DEFAULT_GRAPH.y0 + DEFAULT_GRAPH.y1) / 2;
  const isUp = currentValue >= snapshot.previousClose;

  ctx.clearRect(0, 0, W, H);
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, skin.palette.backgroundTop);
  grad.addColorStop(1, skin.palette.backgroundBottom);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
  drawDecorations(ctx, skin, motionProgress, assets);
  fillRounded(ctx, 48, 48, 984, 984, 44, skin.palette.card, "rgba(255,255,255,.16)");
  drawDirectionalSeason(ctx, skin, snapshot.close >= snapshot.previousClose, motionProgress);

  ctx.fillStyle = skin.palette.foreground;
  ctx.font = `${skin.typography.titleWeight} 78px ${skin.typography.family}`;
  ctx.fillText(snapshot.label, 92, 124);
  ctx.fillStyle = skin.palette.muted;
  ctx.font = `500 27px ${skin.typography.family}`;
  ctx.fillText(`${dateJa(snapshot.date)} / ${scenarioJa(scenarioLabel)}`, 96, 170);

  fillRounded(ctx, 78, 205, 924, 555, 32, skin.palette.panel, "rgba(255,255,255,.14)");
  ctx.save();
  ctx.beginPath();
  roundedRect(ctx, 78, 205, 924, 555, 32);
  ctx.clip();
  const zone = ctx.createLinearGradient(0, graphMidY, 0, isUp ? DEFAULT_GRAPH.y0 : DEFAULT_GRAPH.y1);
  zone.addColorStop(0, hexToRgba(isUp ? skin.palette.up : skin.palette.down, 0.04));
  zone.addColorStop(1, hexToRgba(isUp ? skin.palette.up : skin.palette.down, 0.12));
  ctx.fillStyle = zone;
  ctx.fillRect(DEFAULT_GRAPH.x0, isUp ? DEFAULT_GRAPH.y0 : graphMidY, DEFAULT_GRAPH.x1 - DEFAULT_GRAPH.x0, Math.abs(graphMidY - (isUp ? DEFAULT_GRAPH.y0 : DEFAULT_GRAPH.y1)));
  drawAxis(ctx, skin, scale, {
    referenceValue: snapshot.previousClose,
    referenceLabel: "前日終値",
    referenceDecimals: decimalsFor(snapshot),
    xTicks: DAILY_TIME_TICKS
  });
  drawPath(ctx, skin, points, scale, visibleCount, snapshot.previousClose);
  ctx.restore();
  drawTip(ctx, snapshot, skin, currentPoint, scale, finishProgress, holdProgress, settings, assets);
  drawReactionAccentAsset(ctx, skin, assets, snapshot.close >= snapshot.previousClose, finishProgress, holdProgress);
  drawLiveNumbers(ctx, snapshot, skin, currentValue, finishProgress);

  const finalColor = snapshot.change < 0 ? skin.palette.down : skin.palette.up;
  const footerText = finishProgress > 0.7
    ? (snapshot.change < 0 ? "マイナスで終了" : "プラスで終了")
    : (currentValue < snapshot.previousClose ? "前日終値を下回っています" : "前日終値を上回っています");
  const footer = { x: 78, y: 952, w: 924, h: 54, r: 20 };
  fillRounded(ctx, footer.x, footer.y, footer.w, footer.h, footer.r, hexToRgba(finalColor, 0.12), finalColor, 2);
  ctx.fillStyle = finalColor;
  ctx.font = `800 25px ${skin.typography.family}`;
  ctx.textBaseline = "middle";
  ctx.fillText(footerText, footer.x + 30, footer.y + footer.h / 2);
  ctx.textBaseline = "alphabetic";
}

export function renderWeeklyFrame(
  ctx: CanvasRenderingContext2D,
  weekly: WeeklySummary,
  skin: MotionSkin,
  progress = 1,
  scale?: ScaleState,
  visibleCountOverride?: number,
  finishProgressOverride?: number,
  holdProgressOverride?: number,
  settings: MotionRenderSettings = DEFAULT_RENDER_SETTINGS,
  assets?: LoadedSkinAssets
): void {
  const decimals = weekly.decimals ?? 2;
  const clampedProgress = Math.max(0, Math.min(1, progress));
  const lineProgress = Math.min(1, clampedProgress / 0.72);
  const statsProgress = finishProgressOverride ?? Math.max(0, (clampedProgress - 0.62) / 0.38);
  const holdProgress = holdProgressOverride ?? Math.max(0, (clampedProgress - 0.86) / 0.14);
  const values = [weekly.low, weekly.high, weekly.previousWeekClose, ...weekly.points.map((point) => point.y)];
  const low = Math.min(...values);
  const high = Math.max(...values);
  const activeScale = scale ?? {
    prev: weekly.previousWeekClose,
    halfRange: Math.max(Math.abs(high - weekly.previousWeekClose), Math.abs(low - weekly.previousWeekClose), 1) * 1.12,
    yMin: low,
    yMax: high
  };
  const requestedVisibleCount = visibleCountOverride ?? Math.max(2, Math.ceil(weekly.points.length * lineProgress));
  const renderPoints = prepareWeeklyRenderPoints(weekly.points);
  const visibleRatio = weekly.points.length <= 1 ? 1 : (requestedVisibleCount - 1) / (weekly.points.length - 1);
  const visibleCount = Math.max(2, Math.min(renderPoints.length, Math.ceil(1 + (renderPoints.length - 1) * visibleRatio)));
  const visiblePoints = renderPoints.slice(0, visibleCount);
  const currentPoint = visiblePoints[Math.min(visiblePoints.length - 1, renderPoints.length - 1)] ?? renderPoints[renderPoints.length - 1];
  const currentValue = currentPoint.y;
  const graphMidY = (DEFAULT_GRAPH.y0 + DEFAULT_GRAPH.y1) / 2;
  const isUpNow = currentValue >= weekly.previousWeekClose;
  const isUpWeek = weekly.close >= weekly.previousWeekClose;
  const displayWeekEnd = weekly.displayWeekEnd ?? weekly.weekEnd;
  const tradingWeekEnd = weekly.tradingWeekEnd ?? weekly.dataAsOf;
  const weeklySnapshot: MarketSnapshot = {
    symbolId: weekly.symbolId,
    label: symbolDisplayName(weekly.symbolId),
    shortLabel: symbolDisplayName(weekly.symbolId),
    market: "",
    currency: "USD",
    timezone: "",
    status: weekly.status,
    source: weekly.source,
    dataAsOf: weekly.dataAsOf,
    retrievedAt: weekly.retrievedAt,
    pathSource: weekly.seriesSource === "yahoo-chart-60m" ? "real_intraday" : "synthetic_from_ohlc",
    previousClose: weekly.previousWeekClose,
    open: weekly.open,
    high: weekly.high,
    low: weekly.low,
    close: weekly.close,
    change: weekly.change,
    changeRate: weekly.changeRate,
    date: tradingWeekEnd,
    decimals
  };
  const dateTicks = weekly.daily.map((bar, index) => ({
    label: shortDateJa(bar.date),
    x: weekly.points.find((point) => point.date === bar.date)?.x ?? (index / Math.max(1, weekly.daily.length - 1))
  }));
  const xTicks = weekly.holiday
    ? [...dateTicks, { label: shortDateJa(weekly.holiday.date), x: 1 }]
    : dateTicks;

  ctx.clearRect(0, 0, W, H);
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, skin.palette.backgroundTop);
  grad.addColorStop(1, skin.palette.backgroundBottom);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
  drawDecorations(ctx, skin, clampedProgress, assets);
  fillRounded(ctx, 48, 48, 984, 984, 44, skin.palette.card, "rgba(255,255,255,.16)");
  drawDirectionalSeason(ctx, skin, isUpWeek, clampedProgress);

  ctx.fillStyle = skin.palette.foreground;
  ctx.font = `${skin.typography.titleWeight} 78px ${skin.typography.family}`;
  ctx.fillText(symbolDisplayName(weekly.symbolId), 92, 132);
  ctx.fillStyle = skin.palette.muted;
  ctx.font = `500 27px ${skin.typography.family}`;
  ctx.fillText(`${dateJa(weekly.weekStart)}〜${shortDateJa(displayWeekEnd)} / 週間`, 96, 180);

  fillRounded(ctx, 78, 205, 924, 555, 32, skin.palette.panel, "rgba(255,255,255,.14)");
  ctx.save();
  ctx.beginPath();
  roundedRect(ctx, 78, 205, 924, 555, 32);
  ctx.clip();
  const zone = ctx.createLinearGradient(0, graphMidY, 0, isUpNow ? DEFAULT_GRAPH.y0 : DEFAULT_GRAPH.y1);
  zone.addColorStop(0, hexToRgba(isUpNow ? skin.palette.up : skin.palette.down, 0.035));
  zone.addColorStop(1, hexToRgba(isUpNow ? skin.palette.up : skin.palette.down, 0.1));
  ctx.fillStyle = zone;
  ctx.fillRect(DEFAULT_GRAPH.x0, isUpNow ? DEFAULT_GRAPH.y0 : graphMidY, DEFAULT_GRAPH.x1 - DEFAULT_GRAPH.x0, Math.abs(graphMidY - (isUpNow ? DEFAULT_GRAPH.y0 : DEFAULT_GRAPH.y1)));
  drawAxis(ctx, skin, activeScale, {
    referenceValue: weekly.previousWeekClose,
    referenceLabel: "前週末終値",
    referenceDecimals: decimals,
    xTicks
  });
  drawPath(ctx, skin, renderPoints, activeScale, visibleCount, weekly.previousWeekClose);

  if (weekly.holiday && statsProgress > 0.2) {
    const holidayX = DEFAULT_GRAPH.x1 - 1;
    const pulse = 0.5 + 0.5 * Math.sin(Math.max(holdProgress, statsProgress) * Math.PI * 3.2);
    ctx.save();
    ctx.globalAlpha = Math.min(1, statsProgress * 1.2);
    const shade = ctx.createLinearGradient(DEFAULT_GRAPH.x0, 0, DEFAULT_GRAPH.x1, 0);
    shade.addColorStop(0, "rgba(6,9,18,0)");
    shade.addColorStop(0.72, `rgba(6,9,18,${0.04 + pulse * 0.04})`);
    shade.addColorStop(1, `rgba(6,9,18,${0.18 + pulse * 0.08})`);
    ctx.fillStyle = shade;
    ctx.fillRect(DEFAULT_GRAPH.x0, DEFAULT_GRAPH.y0, DEFAULT_GRAPH.x1 - DEFAULT_GRAPH.x0, DEFAULT_GRAPH.y1 - DEFAULT_GRAPH.y0);
    ctx.setLineDash([9, 10]);
    ctx.strokeStyle = hexToRgba(skin.palette.accent, 0.54 + pulse * 0.18);
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(holidayX, DEFAULT_GRAPH.y0 + 8);
    ctx.lineTo(holidayX, DEFAULT_GRAPH.y1 - 8);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  ctx.restore();
  drawTip(ctx, weeklySnapshot, skin, currentPoint, activeScale, statsProgress, holdProgress, settings, assets);
  drawReactionAccentAsset(ctx, skin, assets, isUpWeek, statsProgress, holdProgress);

  if (weekly.holiday && statsProgress > 0.2) {
    const boxX = DEFAULT_GRAPH.x1 - 250;
    fillRounded(ctx, boxX, DEFAULT_GRAPH.y0 + 26, 226, weekly.holiday.nextOpenLabel ? 88 : 66, 20, skin.palette.panel, skin.palette.accent);
    ctx.fillStyle = skin.palette.accent;
    ctx.font = `800 21px ${skin.typography.family}`;
    ctx.textAlign = "left";
    ctx.fillText("マーケット休場中", boxX + 20, DEFAULT_GRAPH.y0 + 53);
    ctx.fillStyle = skin.palette.muted;
    ctx.font = `700 16px ${skin.typography.family}`;
    ctx.fillText(weekly.holiday.label, boxX + 20, DEFAULT_GRAPH.y0 + 77);
    if (weekly.holiday.nextOpenLabel) ctx.fillText(weekly.holiday.nextOpenLabel, boxX + 20, DEFAULT_GRAPH.y0 + 99);
  }

  const stats = [
    [`${shortDateJa(tradingWeekEnd)} 終値`, money(weekly.close, decimals), skin.palette.foreground],
    ["週間の変動", signedMoney(weekly.change, decimals), weekly.change >= 0 ? skin.palette.up : skin.palette.down],
    ["騰落率", signedPercent(weekly.changeRate), weekly.change >= 0 ? skin.palette.up : skin.palette.down]
  ];
  stats.forEach(([label, value], index) => {
    const gap = 20;
    const cardW = (924 - gap * 2) / 3;
    const x = 78 + index * (cardW + gap);
    const y = 786;
    ctx.save();
    ctx.globalAlpha = Math.min(1, statsProgress * 1.35);
    fillRounded(ctx, x, y, cardW, 142, 28, "rgba(255,255,255,.18)", "rgba(255,255,255,.16)");
    ctx.fillStyle = skin.palette.muted;
    ctx.font = `700 25px ${skin.typography.family}`;
    ctx.fillText(label, x + 28, y + 44);
    ctx.fillStyle = String(value).startsWith("+") ? skin.palette.up : String(value).startsWith("-") ? skin.palette.down : skin.palette.foreground;
    ctx.font = `800 ${index === 0 ? 50 : 56}px ${skin.typography.family}`;
    ctx.fillText(value, x + 28, y + 105);
    ctx.restore();
  });

  const finalColor = weekly.change >= 0 ? skin.palette.up : skin.palette.down;
  ctx.save();
  ctx.globalAlpha = Math.min(1, statsProgress * 1.35);
  const footer = { x: 78, y: 952, w: 924, h: 54, r: 20 };
  fillRounded(ctx, footer.x, footer.y, footer.w, footer.h, footer.r, hexToRgba(finalColor, 0.13), finalColor, 2);
  ctx.fillStyle = finalColor;
  ctx.font = `800 24px ${skin.typography.family}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(weekly.change >= 0 ? "今週はプラスで終了" : "今週はマイナスで終了", footer.x + 30, footer.y + footer.h / 2);
  ctx.fillStyle = skin.palette.muted;
  ctx.font = `600 18px ${skin.typography.family}`;
  ctx.textBaseline = "alphabetic";
  ctx.fillText(`ベスト ${shortDateJa(weekly.bestDay.date)} ${signedPercent(weekly.bestDay.changeRate)}   ワースト ${shortDateJa(weekly.worstDay.date)} ${signedPercent(weekly.worstDay.changeRate)}`, 108, 1024);
  ctx.restore();
  ctx.textAlign = "left";
}


